/**
 * What a feature panel may *do* on its device (P1): run a command and follow it to the end,
 * audit a reveal of masked data, and open a live session. The shell implements this port over
 * the typed client and provides it per device page; features never fetch.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { LiveInputEventJson, LiveRtcMessage, LiveServerMessage, LiveTransport, LiveViewerMessage } from "@tether/contracts/api";
import type { Cap, CapState, Command, Device, DeviceConsentResponse, FilesBrowseResponse, FilesIntegrityResponse, FilesSnapshot, FilesTransfer, PoliciesEffectiveResponse, RecordItem, SettingsResponse, TetherRequest } from "./api";

export type { LiveInputEventJson, LiveRtcMessage, LiveServerMessage, LiveTransport, LiveViewerMessage };

/** How a command ended. `queued` means the device is offline and it will run when it connects. */
export type CommandOutcome =
  | { readonly kind: "done"; readonly result: unknown; readonly command: Command }
  | { readonly kind: "failed"; readonly code: string; readonly message: string; readonly command?: Command };

/** How sending a command went: the server took it (its progress shows in the activity tray), or refused it. */
export type CommandSent =
  | { readonly kind: "sent"; readonly command: Command }
  | { readonly kind: "failed"; readonly code: string; readonly message: string };

export interface RunOptions {
  /** Seconds before the command expires (default 300). */
  readonly deadline_s?: number;
}

/** One open viewer socket. Binary frames arrive as Blobs (JPEG); text carries control and WebRTC signalling. */
export interface LiveSocket {
  send(msg: LiveViewerMessage): void;
  close(): void;
}
export interface LiveSocketHandlers {
  onOpen(): void;
  onMessage(msg: LiveServerMessage): void;
  onFrame(jpeg: Blob): void;
  onClose(code: number, reason: string): void;
}
export interface LivePort {
  start(opts: { control: boolean }): Promise<{ session_id: string }>;
  end(sessionId: string): Promise<void>;
  connect(sessionId: string, handlers: LiveSocketHandlers): LiveSocket;
}

/**
 * Files from the device (P2 §1.3): a download is a server-side transfer the device streams into;
 * its bytes are then fetched by URL (elements can't send headers, so the URL carries the token).
 */
export interface FilesPort {
  /** Start copying `path` from the device to the server. */
  download(path: string): Promise<{ readonly transfer_id: string }>;
  transfer(id: string): Promise<FilesTransfer>;
  cancel(id: string): Promise<void>;
  /** Whether the bytes were checked on arrival, and where an interrupted copy can continue. Absent on hosts that don't offer it. */
  integrity?(id: string): Promise<FilesIntegrityResponse>;
  /** The transfer's bytes: an attachment, or `inline` for `<img>` / `<video>` / `<audio>` / PDF. */
  contentUrl(id: string, opts?: { readonly inline?: boolean }): string;
  /** Send the browser to the attachment URL (the browser saves the file). */
  save(id: string): void;
}

/** A read the shell does for a panel (TanStack Query underneath; SSE keeps it fresh). */
export interface DataResult<T> {
  readonly data: T | undefined;
  readonly isPending: boolean;
  readonly isError: boolean;
  refetch(): void;
}
export interface PagedDataResult<T> extends DataResult<T> {
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  fetchNext(): void;
}
/**
 * Kernel and server reads a panel may make beyond its own declared source. These are hooks:
 * call them unconditionally at the top of a component (see the use* wrappers below).
 */
export interface DeviceDataPort {
  /** Synced records of any collection on this device (kernel data; never another feature's code).
   * `query.pkg` narrows package-keyed collections server-side (e.g. "screen.chat"). */
  useRecords(coll: string, enabled?: boolean, query?: { readonly pkg?: string; readonly key_from?: string; readonly key_to?: string }): PagedDataResult<readonly RecordItem[]>;
  /** Server settings (map tiles, senders). */
  useSettings(): DataResult<SettingsResponse>;
  /** The device's effective behaviour policy. */
  useEffectivePolicy(): DataResult<PoliciesEffectiveResponse>;
  /** The device's screen windows (consent ledger), newest first. */
  useScreenWindows(): DataResult<DeviceConsentResponse>;
  /** P3: the stored files tree from the last "Fetch tree"; `null` when none was ever fetched. */
  useFilesSnapshot(): DataResult<FilesSnapshot | null>;
  /** One folder from the saved tree, fetched in bounded server-side pages. */
  useFilesBrowse(path: string): PagedDataResult<FilesBrowseResponse | null>;
}

export interface RequestOptions {
  readonly label?: string;
  readonly expires_in_s?: number;
  /** P5 §3: the device's name as the admin typed it; required for wipe, passcode and deprovision. */
  readonly confirm?: string;
}

/**
 * P3 §2: requests that wait for the device. Each admin read (fetch data, a folder tree, a preview,
 * a download) is a request; while the device is offline it waits and runs when it connects.
 * The lists stay fresh through SSE `request.updated`.
 */
export interface RequestsPort {
  /** Create (or get back the identical waiting) request. Rejects when the server refuses it. */
  create(kind: string, args: unknown, opts?: RequestOptions): Promise<TetherRequest>;
  cancel(id: string): Promise<TetherRequest>;
  retry(id: string): Promise<TetherRequest>;
  /** Hook: this device's requests, newest first. */
  useList(): DataResult<readonly TetherRequest[]>;
  /** Hook: one request with its result (null id: nothing to read). */
  useOne(id: string | null): DataResult<TetherRequest>;
}

export interface DeviceServices {
  readonly device: Device;
  readonly caps: readonly Cap[];
  /** The state of one capability: reported, or "not_built" when the device reported others, else "unknown". */
  capState(name: string): { readonly state: CapState | "unknown"; readonly fix?: string };
  /** Create a command and resolve when it is done, failed or expired. Never rejects. */
  run(name: string, args?: unknown, opts?: RunOptions): Promise<CommandOutcome>;
  /** Create a command and resolve once the server took it (or refused it); the tray follows it. Never rejects. */
  send(name: string, args?: unknown, opts?: RunOptions): Promise<CommandSent>;
  /** Audit that the viewer revealed masked `special` data (RFC-008 §9.5). Rejects when the audit write fails. */
  reveal(input: { readonly coll: string; readonly key: string; readonly reason?: string }): Promise<void>;
  readonly live: LivePort;
  readonly files: FilesPort;
  readonly data: DeviceDataPort;
  readonly requests: RequestsPort;
}

const ServicesContext = createContext<DeviceServices | null>(null);

export function DeviceServicesProvider({ value, children }: { value: DeviceServices; children: ReactNode }) {
  return <ServicesContext.Provider value={value}>{children}</ServicesContext.Provider>;
}

export function useDeviceServices(): DeviceServices {
  const s = useContext(ServicesContext);
  if (!s) throw new Error("useDeviceServices outside a device page");
  return s;
}

export const useDeviceRecords = (coll: string, enabled = true) => useDeviceServices().data.useRecords(coll, enabled);
export const useServerSettings = () => useDeviceServices().data.useSettings();
export const useEffectivePolicy = () => useDeviceServices().data.useEffectivePolicy();
export const useScreenWindows = () => useDeviceServices().data.useScreenWindows();
export const useFilesSnapshot = () => useDeviceServices().data.useFilesSnapshot();
export const useFilesBrowse = (path: string) => useDeviceServices().data.useFilesBrowse(path);

export type CommandRunState =
  | { readonly status: "idle" }
  /** The create call is in flight (the button shows a spinner). */
  | { readonly status: "running"; readonly offline: boolean }
  | CommandSent;

/**
 * A button-sized command sender: `run(args)` creates the command and the latest state says whether
 * the server took it. How it goes on the device shows in the shell's activity tray, not here.
 */
export function useDeviceCommand(name: string) {
  const services = useDeviceServices();
  const [state, setState] = useState<CommandRunState>({ status: "idle" });
  const seq = useRef(0);
  const alive = useRef(true);
  useEffect(() => () => void (alive.current = false), []);
  const run = useCallback(
    async (args: unknown = {}, opts?: RunOptions): Promise<CommandSent> => {
      const mine = ++seq.current;
      setState({ status: "running", offline: services.device.state !== "online" });
      const out = await services.send(name, args, opts);
      if (alive.current && mine === seq.current) setState(out);
      return out;
    },
    [services, name],
  );
  const reset = useCallback(() => setState({ status: "idle" }), []);
  return { state, run, reset, cap: services.capState(name) } as const;
}

/** Plain-words copy for a finished or failed command (P1-mobile.md §3 error codes). */
export function describeFailure(code: string, message: string): string {
  switch (code) {
    case "denied":
      return `The device needs a permission for this: ${message}`;
    case "unsupported":
      return "This device's operating system doesn't allow this.";
    case "consent_declined":
      return "The holder declined on the device.";
    case "busy":
      return "The device is busy with another request. Try again in a moment.";
    case "not_found":
      return "The device couldn't find that.";
    case "expired":
      return "The device didn't answer before the deadline.";
    default:
      return message || "The device couldn't do this.";
  }
}
